import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Pill, Avatar } from "@/components/Bits";
import { toast } from "sonner";
import { Plus, Sparkle, Camera } from "@phosphor-icons/react";
import PhotoCapture from "@/components/PhotoCapture";
import MediaFile from "@/components/MediaFile";

const MOODS = ["happy", "calm", "playful", "tired", "curious", "excited"];

export default function DailyUpdates() {
  const [updates, setUpdates] = useState([]);
  const [students, setStudents] = useState([]);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/daily-updates"), api.get("/students")]);
    setUpdates(u.data); setStudents(s.data);
  };
  useEffect(() => { load(); }, []);

  const studentMap = Object.fromEntries(students.map(s => [s.student_id, s]));

  return (
    <div>
      <PageHeader
        eyebrow="Daily updates"
        title="Today, in your classroom"
        subtitle="Photos, naps, meals, milestones — sent to parents in seconds."
        action={<button onClick={() => setComposing(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold" data-testid="new-update-btn"><Plus weight="bold" /> New update</button>}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {updates.map(u => {
          const s = studentMap[u.student_id];
          return (
            <div key={u.update_id} className="bg-white rounded-3xl border border-border p-5" data-testid={`update-${u.update_id}`}>
              <div className="flex items-center gap-3">
                {s ? <Avatar name={s.name} /> : <div className="w-10 h-10 rounded-full bg-paper-alt" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s?.name || "Student"}</div>
                  <div className="text-xs text-ink-soft">{u.date}</div>
                </div>
                <Pill tone="mustard">{u.mood}</Pill>
              </div>
              {u.activities?.length > 0 && (
                <div className="mt-3 space-y-1 text-sm">
                  {u.activities.slice(0, 3).map((a, i) => <div key={i}>• {a}</div>)}
                </div>
              )}
              {u.media?.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-1.5">
                  {u.media.slice(0, 6).map((fid) => (
                    <MediaFile key={fid} fileId={fid} className="w-full h-20 rounded-xl object-cover bg-paper-alt" />
                  ))}
                </div>
              )}
              {u.milestones?.length > 0 && (
                <div className="mt-3 p-3 rounded-2xl bg-sage-soft text-sage-deep text-xs">
                  <strong>Milestone:</strong> {u.milestones[0]}
                </div>
              )}
              {u.meals && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-paper-alt p-2 rounded-xl"><div className="text-ink-soft">B'fast</div><div className="font-semibold capitalize">{u.meals.breakfast}</div></div>
                  <div className="bg-paper-alt p-2 rounded-xl"><div className="text-ink-soft">Lunch</div><div className="font-semibold capitalize">{u.meals.lunch}</div></div>
                  <div className="bg-paper-alt p-2 rounded-xl"><div className="text-ink-soft">Nap</div><div className="font-semibold capitalize">{u.nap?.quality}</div></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {composing && <ComposeModal students={students} onClose={() => setComposing(false)} onDone={() => { setComposing(false); load(); }} />}
    </div>
  );
}

function ComposeModal({ students, onClose, onDone }) {
  const [f, setF] = useState({ student_id: students[0]?.student_id || "", mood: "happy", activities: "" });
  const [media, setMedia] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      student_id: f.student_id,
      mood: f.mood,
      activities: f.activities.split("\n").filter(Boolean),
      meals: { breakfast: "finished", lunch: "finished" },
      nap: { quality: "restful" },
      media,
    };
    try { await api.post("/daily-updates", payload); toast.success("Update sent to parents"); onDone(); }
    catch { toast.error("Could not save"); }
  };

  const aiPolish = async () => {
    if (!f.activities.trim()) return;
    const child = students.find(s => s.student_id === f.student_id);
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/observation-note", { child_name: child?.name || "Child", note: f.activities });
      setF({ ...f, activities: data.text });
      toast.success("Polished by MangoCubs AI");
    } catch { toast.error("AI is napping"); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl border border-border p-6 space-y-3 my-auto" data-testid="compose-update-modal">
        <h3 className="font-heading text-xl font-bold">New daily update</h3>
        <select required value={f.student_id} onChange={e=>setF({...f, student_id: e.target.value})} className="w-full h-11 px-3 rounded-xl border border-border">
          {students.map(s => <option key={s.student_id} value={s.student_id}>{s.name} — {s.class_name}</option>)}
        </select>
        <select value={f.mood} onChange={e=>setF({...f, mood: e.target.value})} className="w-full h-11 px-3 rounded-xl border border-border">
          {MOODS.map(m => <option key={m}>{m}</option>)}
        </select>
        <textarea rows={5} value={f.activities} onChange={e=>setF({...f, activities: e.target.value})} placeholder="What happened today? (one activity per line)" className="w-full px-3 py-2 rounded-xl border border-border" data-testid="update-activities" />
        <div>
          <div className="text-sm font-medium text-ink mb-1.5 flex items-center gap-1"><Camera weight="duotone" /> Add photos / video</div>
          <PhotoCapture
            accept="image/*,video/mp4"
            multiple
            label="Add up to 6 photos or a short video"
            onUploaded={(ids) => setMedia(ids || [])}
            testid="update-media"
          />
        </div>
        <button type="button" onClick={aiPolish} disabled={aiLoading} className="w-full h-10 rounded-full bg-ink text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="update-ai-polish">
          <Sparkle weight="duotone" /> {aiLoading ? "Polishing..." : "Polish with MangoCubs AI"}
        </button>
        <div className="flex gap-2"><button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border">Cancel</button><button className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold" data-testid="update-submit">Send to parents</button></div>
      </form>
    </div>
  );
}
